import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SingleLawPostProps } from 'lib/queries/state-law';
import type { StateLawsPostACF } from 'types';
import React from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Button, Card, Paragraph, Wrap } from '@leafwell/components';
import Blocks from 'components/blocks';
import CardGradientHalf from 'components/card-gradient-half';
import WysiwygContent from 'components/wysiwyg-content';
import HeadingBody from 'components/heading-body';
import Line from 'components/line';
import Loader from 'components/loader';
import PageWrap from 'components/page-wrap';
import ArrowButton from 'components/arrow-button';
import { getSingleStateLaw } from 'lib/queries/state-law';
import { useWordPressContext } from 'contexts/wordpress-provider';

const ContentsTable = dynamic(() => import('components/contents-table'));

const StatusCard: React.FC<{
  footer?: { positive: boolean; label: string }[];
  info?: { label: string; value: string; extraInfo?: string }[];
  title: string;
}> = ({ footer, info, title }) => (
  <Card rounded variant="light" className="grid-rows-card-small">
    <HeadingBody className="pb-3 lg:pb-6" level="4" title={title} />
    {info && (
      <ul className="grid gap-y-3 lg:gap-y-6">
        {info.map(({ label, value, extraInfo }) => (
          <Paragraph
            size="small"
            tagName="li"
            className="grid sm:grid-cols-2 sm:justify-between sm:gap-x-3"
          >
            {label}
            <p className="!m-0 sm:justify-self-end sm:text-right">
              <span className="sm:font-medium">{value}</span>
              {extraInfo && (
                <Paragraph
                  tagName="span"
                  size="xsmall"
                  variant="medium"
                  className="pl-2 sm:pl-0 sm:block"
                >
                  {extraInfo}
                </Paragraph>
              )}
            </p>
          </Paragraph>
        ))}
      </ul>
    )}
    {info && footer && <Line />}
    {footer && (
      <ul className="grid gap-y-3">
        {footer.map(({ label, positive }, key) => (
          <li key={key} className="grid grid-flow-col gap-x-6 justify-between">
            {label}
            <Image
              src={positive ? '/check-green.svg' : '/not-allowed.svg'}
              alt=""
              height={24}
              width={24}
              className="self-center"
            />
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const ApplicationProcess: React.FC<
  StateLawsPostACF['applicationProcess'] & {
    slug?: string;
  }
> = ({ description, extraInfo, title, slug: pageSlug }) => {
  const { statePages, translations } = useWordPressContext();

  return (
    <CardGradientHalf className="md:grid-cols-3 md:gap-x-10 xl:py-0 xl:grid-cols-12">
      <Paragraph size="small" className="xl:pt-10 xl:col-span-2">
        {translations['Application process']}
      </Paragraph>
      <section className="md:col-span-2 xl:col-span-5 grid gap-y-3 lg:gap-y-6 xl:pt-10 md:grid-cols-2-auto md:gap-x-10">
        <Image
          src="/arrow-curly.svg"
          alt=""
          className="hidden md:block align-self-end md:row-start-2"
          width={111}
          height={165}
        />
        <HeadingBody className="md:col-start-2" level="4" title={title} />
        <Paragraph size="small" className="md:col-start-2">
          {description}
        </Paragraph>
      </section>
      <section className="xl:row-start-1 xl:row-end-3 md:col-start-3 xl:col-start-8 xl:col-span-5 relative grid xl:grid-cols-2 gap-y-6 md:gap-y-3 content-end justify-items-start md:justify-items-end md:text-right">
        <div className="absolute pointer-events-none hidden xl:block xl:bottom-0 xl:w-full xl:h-full-10 xl:row-span-2">
          <Image
            src="/hand-card.png"
            alt=""
            className="object-contain object-left-bottom w-full h-full"
            sizes="100vw"
            width={0}
            height={0}
          />
        </div>
        <Paragraph size="small" className="xl:col-start-2">
          {extraInfo}
        </Paragraph>
        <Button
          className="justify-self-center md:justify-self-end xl:col-start-2 xl:mb-10 hover:!bg-black hover:!border-black hover:!text-white"
          label={translations['Get your card']}
          href={statePages.find(({ slug }) => slug === pageSlug)?.uri}
          variant="light"
          rounded
        />
      </section>
    </CardGradientHalf>
  );
};

const SingleLawContent: React.FC<SingleLawPostProps> = ({
  post: {
    title,
    slug,
    modified,
    content,
    seo,
    stateLawsStatus,
    acf: {
      pageType,
      states,
      blocks,
      applicationProcess,
      cultivation,
      possession,
      taxes,
      tableOfContents,
    },
  },
}) => {
  const { translations } = useWordPressContext();

  return (
    <PageWrap seo={seo} title={title} pageType={pageType}>
      <header className="relative before:bg-gray-100 before:absolute before:bottom-0 before:left-0 before:w-screen before:h-screen">
        <Wrap className="relative items-center !gap-y-2">
          <Paragraph size="small">
            State laws
            <Image
              src="/us-flag.svg"
              className="w-4 ml-2 inline-block align-baseline"
              alt=""
              width={16}
              height={9}
            />
          </Paragraph>
          <Paragraph
            tagName="h1"
            className="uppercase text-4xl sm:text-5xl lg:text-6xl xl:text-8xl lg:leading-none"
          >
            {title}
          </Paragraph>
        </Wrap>
      </header>
      <Wrap
        className={[
          '!gap-y-6 sm:gap-x-6 lg:mt-12',
          'sm:grid-cols-2 md:auto-cols-fr md:grid-flow-col xl:gap-x-8',
        ].join(' ')}
      >
        <StatusCard
          title={translations['Legal status']}
          footer={
            stateLawsStatus
              ? stateLawsStatus.map(({ name, checkIcon }) => ({
                  label: name,
                  positive: checkIcon,
                }))
              : null
          }
        />
        <StatusCard
          title={translations['Possession Limits']}
          info={possession.info}
          footer={
            possession.legal
              ? null
              : [
                  {
                    label: translations['Possession is %s'].replace(
                      '%s',
                      translations['not allowed'],
                    ),
                    positive: false,
                  },
                ]
          }
        />
        {taxes && (
          <StatusCard title={translations['State taxes']} info={taxes} />
        )}
        {cultivation.show && (
          <StatusCard
            title={translations['Cultivation']}
            info={cultivation.legal ? cultivation.info : null}
            footer={[
              {
                label: translations['Cultivation is %s'].replace(
                  '%s',
                  cultivation.legal
                    ? translations['legal']
                    : translations['illegal'],
                ),
                positive: cultivation.legal,
              },
            ]}
          />
        )}
      </Wrap>
      {applicationProcess?.title?.length > 0 && (
        <Wrap>
          {tableOfContents.length ? (
            <ArrowButton
              title={translations['Learn more']}
              href={`#${tableOfContents[0]?.id}`}
              className="justify-self-center"
              classNameImage="rotate-90"
            />
          ) : null}
          <ApplicationProcess slug={slug} {...applicationProcess} />
        </Wrap>
      )}
      <Wrap
        className="lg:grid-cols-12 lg:gap-x-12 xl:gap-x-24 items-start"
        tagName="article"
      >
        <ContentsTable
          className="lg:order-2 lg:col-span-4"
          items={tableOfContents}
        />
        <WysiwygContent
          className="lg:order-1 lg:col-span-8"
          content={content}
          tagName="section"
        />
      </Wrap>
      <Blocks
        states={states}
        blocks={blocks}
        pageType={pageType}
        modified={modified}
      />
    </PageWrap>
  );
};

const SingleLaw: React.FC<SingleLawPostProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <SingleLawContent {...props} />;
};

export const getStaticProps: GetStaticProps = async context => {
  return await getSingleStateLaw(context);
};

export const getStaticPaths: GetStaticPaths = async context => {
  return { paths: [], fallback: 'blocking' };
};

export default SingleLaw;
